import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EvictSingleDialogProps {
  mode: 'single';
  cacheName: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

interface EvictAllDialogProps {
  mode: 'all';
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

type EvictConfirmDialogProps = EvictSingleDialogProps | EvictAllDialogProps;

/**
 * EvictConfirmDialog — 단건/전체 evict 확인 다이얼로그.
 *
 * 접근성:
 * - role="dialog" + aria-modal="true"
 * - ESC 로 닫기
 * - focus trap (첫 포커스 → 취소 버튼)
 * - 전체 모드: "EVICT-ALL" 입력 정확 일치 시 확인 버튼 활성화
 */
export function EvictConfirmDialog(props: EvictConfirmDialogProps) {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [confirmText, setConfirmText] = useState('');

  const isAll = props.mode === 'all';
  const cacheName = !isAll ? (props as EvictSingleDialogProps).cacheName : '';
  const confirmMatch = isAll ? confirmText === 'EVICT-ALL' : true;

  // focus trap — 취소 버튼으로 초기 포커스
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // ESC 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [props]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
      }}
      aria-hidden="false"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="evict-dialog-title"
        aria-describedby="evict-dialog-desc"
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: isAll ? 480 : 420,
          maxWidth: '90vw',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <h2
          id="evict-dialog-title"
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            marginBottom: 'var(--spacing-3)',
            color: isAll ? 'var(--color-danger)' : 'var(--color-text-primary)',
          }}
        >
          {isAll
            ? t('cacheAdmin.dialog.evictAll.title')
            : t('cacheAdmin.dialog.evict.title')}
        </h2>

        <p
          id="evict-dialog-desc"
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--spacing-4)',
            lineHeight: 1.6,
          }}
        >
          {isAll
            ? t('cacheAdmin.dialog.evictAll.message')
            : t('cacheAdmin.dialog.evict.message', { name: cacheName })}
        </p>

        {/* 전체 evict — EVICT-ALL 타이핑 */}
        {isAll && (
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label
              htmlFor="evict-all-confirm"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('cacheAdmin.dialog.evictAll.confirmPlaceholder')}
            </label>
            <input
              id="evict-all-confirm"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={t('cacheAdmin.dialog.evictAll.confirmPlaceholder')}
              aria-invalid={confirmText.length > 0 && !confirmMatch}
              aria-describedby="evict-all-confirm-hint"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && confirmMatch && !props.isPending) {
                  props.onConfirm();
                }
              }}
              style={{
                width: '100%',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: `2px solid ${confirmText.length > 0 && !confirmMatch ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-mono)',
                boxSizing: 'border-box',
              }}
            />
            <p
              id="evict-all-confirm-hint"
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginTop: 4,
              }}
            >
              정확히 <code>EVICT-ALL</code> 을 입력해야 활성화됩니다
            </p>
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
          <button
            ref={cancelRef}
            type="button"
            onClick={props.onCancel}
            disabled={props.isPending}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('cacheAdmin.dialog.button.cancel')}
          </button>
          <button
            type="button"
            onClick={props.onConfirm}
            disabled={props.isPending || !confirmMatch}
            autoFocus={!isAll}
            aria-disabled={!confirmMatch}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background:
                props.isPending || !confirmMatch
                  ? 'var(--color-surface-muted)'
                  : isAll
                  ? 'var(--color-danger)'
                  : 'var(--color-brand-primary)',
              color:
                props.isPending || !confirmMatch
                  ? 'var(--color-text-muted)'
                  : 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor:
                props.isPending || !confirmMatch ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: props.isPending || !confirmMatch ? 0.6 : 1,
            }}
          >
            {props.isPending ? '...' : t('cacheAdmin.dialog.button.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
